// src/app/[lang]/(auth)/layout.tsx
'use client'

import type { Metadata } from 'next';
import Link from 'next/link';
import { AuthLogo } from '@/components/auth/auth-logo';
import type { Locale } from '@/lib/i18n';
import { getDictionary } from '@/lib/i18n';
import { buttonVariants } from '@/components/ui/button';
import { ChevronLeft } from 'lucide-react';
import { usePathname } from 'next/navigation';
import React, { useState, useEffect, use } from 'react';

// Note: generateMetadata is not supported in client components.
// We keep this conceptual structure, but the metadata would be managed
// in the individual page.tsx files that use this layout.

export default function AuthLayout({
  children,
  params: paramsPromise,
}: {
  children: React.ReactNode;
  params: Promise<{ lang: Locale }>;
}) {
  const params = use(paramsPromise);
  const [dictionary, setDictionary] = useState<any>(null);
  const pathname = usePathname();
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    getDictionary(params.lang).then(setDictionary);
  }, [params.lang]);

  const showBackButton = !pathname.endsWith('/signin');

  if (!dictionary) {
    // You can render a loading skeleton here if you'd like
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4 relative">
      <div className="absolute top-4 left-4">
        {showBackButton && (
           <Link href={`/${params.lang}/signin`} className={buttonVariants({ variant: 'ghost' })}>
              <ChevronLeft className="mr-2 h-4 w-4" />
              {dictionary.auth.ssoPage.backToLoginButton || 'Back to Sign In'}
          </Link>
        )}
      </div>
      <div className="mb-8">
        <AuthLogo currentLocale={params.lang} />
      </div>
      <div className="w-full max-w-md">
        {children}
      </div>
       <div className="absolute bottom-4 text-center text-sm text-muted-foreground">
         <div className="flex gap-x-4 justify-center">
            <Link href={`/${params.lang}/terms`} className="hover:text-primary transition-colors">Terms of Use</Link>
            <Link href={`/${params.lang}/privacy`} className="hover:text-primary transition-colors">Privacy Policy</Link>
         </div>
         <div className="mt-2">
            &copy; {currentYear} {dictionary.footer?.copyright || 'ParkWise. All rights reserved.'}
         </div>
      </div>
    </div>
  );
}
